import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Field } from "@/pages/Income";

/**
 * WallOfWinsCard — opt in/out of the public streak-and-wins wall, plus
 * pick a display handle. Preferences persist server-side so they follow
 * across devices.
 */
export default function WallOfWinsCard({ t }) {
  const { user, setUser } = useAuth();
  const [optIn, setOptIn] = useState(!!user?.wall_opt_in);
  const [handle, setHandle] = useState(user?.wall_handle || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/wall-opt-in", {
        opt_in: optIn,
        handle: handle || null,
      });
      setUser(data);
      setHandle(data?.wall_handle || "");
      toast.success(data?.wall_opt_in ? "You're on the Wall" : "Removed from the Wall");
    } catch (e) {
      console.error("Wall opt-in save failed:", e);
      toast.error(t?.("common.toast_failed") || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card-flat p-6 lg:col-span-2">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="label-eyebrow flex items-center gap-2">
          <Trophy size={12}/> {t("settings.wall")}
        </div>
        <Link
          to="/wall"
          className="text-xs text-[var(--accent-primary)] font-medium inline-flex items-center gap-1 hover:underline"
          data-testid="settings-view-wall-link"
        >
          {t("settings.wall_view")} <ExternalLink size={11}/>
        </Link>
      </div>
      <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5">
        {t("settings.wall_body")}
      </p>

      <label className="flex items-center gap-3 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          className="h-5 w-5 accent-[var(--accent-primary)]"
          data-testid="settings-wall-optin-checkbox"
        />
        <span className="text-sm text-[var(--text-primary)]">
          {t("settings.wall_toggle")}
        </span>
      </label>

      <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
        <Field label={t("settings.wall_handle")}>
          <input
            className="field-input"
            value={handle}
            maxLength={24}
            onChange={(e) => setHandle(e.target.value)}
            placeholder={t("settings.wall_handle_placeholder")}
            disabled={!optIn}
            data-testid="settings-wall-handle"
          />
        </Field>
        <button
          className="btn-primary"
          onClick={save}
          disabled={saving}
          data-testid="settings-wall-save-btn"
        >
          {saving ? t("common.saving") : t("settings.wall_save")}
        </button>
      </div>
    </div>
  );
}
